/**
 * WhatsApp Reply Generator — V138
 *
 * Rendered inside `LeadDetailModal` for `registrations` and `inquiries`.
 * Talks to `/api/admin/whatsapp-reply/generate` (Claude Sonnet 5 via the
 * Emergent LLM key) and produces an editable, ready-to-send WhatsApp
 * message tailored to the record's own fields.
 *
 * Actions
 *  • Generate Reply  — asks the LLM for a fresh draft of the selected template
 *  • Edit            — free-text edit inside the same textarea
 *  • Copy Message    — copies to clipboard
 *  • Send on WhatsApp — opens https://wa.me/<phone>?text=<encoded message>
 *
 * Behaviour
 *  • Reads `phone` from the record; if missing, the Send button is disabled
 *    and a hint is shown.
 *  • Does NOT send anything on its own — the admin fires it from wa.me.
 *  • Purely additive — no other modal flow is altered.
 */
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
    MessageCircle, Loader2, Sparkles, Copy, Send, Pencil,
} from "lucide-react";
import { api } from "../../lib/api";

const TEMPLATES = [
    { id: "received",     label: "Demo Registration Received" },
    { id: "confirmation", label: "Demo Confirmation" },
    { id: "reschedule",   label: "Demo Rescheduling" },
    { id: "requirement",  label: "Requirement Received" },
    { id: "followup",     label: "Follow-up" },
];

// V142 — Language toggle. English is default; Hindi (Devanagari) and Hinglish
// (Roman) are for Indian parents. Persisted per-admin in localStorage so a
// coordinator who prefers Hinglish keeps it on every reply.
const LANGUAGES = [
    { id: "english",  label: "English", short: "EN" },
    { id: "hinglish", label: "Hinglish", short: "HI-EN" },
    { id: "hindi",    label: "हिन्दी", short: "हिं" },
];
const LANG_STORAGE_KEY = "wa_reply_language";

function toWaLink(phone, text) {
    if (!phone) return "";
    const digits = String(phone).replace(/[^\d]/g, "");
    if (!digits) return "";
    const q = text ? `?text=${encodeURIComponent(text)}` : "";
    return `https://wa.me/${digits}${q}`;
}

export default function WhatsAppReplyPanel({ kind, item, onLogged }) {
    // kind is the modal's URL kind ("registrations" | "inquiries"); the API
    // expects singular "registration" / "inquiry".
    const apiKind = kind === "registrations" ? "registration"
                   : kind === "inquiries"    ? "inquiry"
                   : null;

    const initialPhone = item.phone || item.whatsapp || item.mobile || "";
    const [templateType, setTemplateType] = useState("received");
    const [language, setLanguage] = useState(() => {
        try { return localStorage.getItem(LANG_STORAGE_KEY) || "english"; }
        catch { return "english"; }
    });
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const [dirty, setDirty] = useState(false);

    const canSend = useMemo(
        () => !!initialPhone && message.trim().length > 0 && !busy,
        [initialPhone, message, busy],
    );

    if (!apiKind) return null;

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/whatsapp-reply/generate", {
                kind: apiKind,
                record_id: item.id,
                template_type: templateType,
                language,
            });
            setMessage(data.message || "");
            setDirty(false);
            const langLabel = LANGUAGES.find((l) => l.id === (data.language || language))?.label || "";
            toast.success(`Reply drafted${langLabel ? ` in ${langLabel}` : ""} — edit before sending if needed`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to generate reply");
        } finally {
            setBusy(false);
        }
    };

    const changeLanguage = (next) => {
        setLanguage(next);
        try { localStorage.setItem(LANG_STORAGE_KEY, next); } catch { /* ignore */ }
    };

    /** V143 — Fire-and-forget paper trail. Runs on the Send-on-WhatsApp
     * click so every dispatched reply lands in the lead's Activity Log
     * with a "sent-on-wa" tag. Never blocks navigation to wa.me. */
    const logSent = () => {
        if (!message.trim()) return;
        api.post("/admin/whatsapp-reply/log-sent", {
            kind: apiKind,
            record_id: item.id,
            template_type: templateType,
            template_label: TEMPLATES.find((t) => t.id === templateType)?.label,
            language,
            language_label: LANGUAGES.find((l) => l.id === language)?.label,
            message,
        }).then((res) => {
            if (res?.data?.notes_log) onLogged?.(res.data.notes_log);
            toast.success("Logged in Activity");
        }).catch(() => { /* silent — WhatsApp still opened */ });
    };

    const copy = async () => {
        if (!message.trim()) return;
        try {
            await navigator.clipboard.writeText(message);
            toast.success("Message copied");
        } catch {
            toast.error("Clipboard blocked — please copy manually");
        }
    };

    const waLink = toWaLink(initialPhone, message);
    const templateLabel = TEMPLATES.find((t) => t.id === templateType)?.label || "";

    return (
        <div className="px-6 pb-6" data-testid="wa-reply-panel">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/30 overflow-hidden">
                <div className="px-4 py-3 border-b border-emerald-200 bg-white flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 text-emerald-800">
                        <MessageCircle className="w-4 h-4" />
                        <span className="text-sm font-semibold">WhatsApp Reply Generator</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span
                            data-testid="wa-reply-language-badge"
                            className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#1E3A8A] bg-blue-100 rounded-full px-2 py-0.5">
                            {LANGUAGES.find((l) => l.id === language)?.short || "EN"}
                        </span>
                        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                            AI Personalised
                        </span>
                    </div>
                </div>

                <div className="p-4 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 sm:mr-1">
                            Template
                        </label>
                        <select
                            value={templateType}
                            onChange={(e) => setTemplateType(e.target.value)}
                            data-testid="wa-reply-template"
                            className="text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white focus:border-emerald-500 focus:outline-none flex-1 min-w-[10rem]">
                            {TEMPLATES.map((t) => (
                                <option key={t.id} value={t.id}>{t.label}</option>
                            ))}
                        </select>
                        <select
                            value={language}
                            onChange={(e) => changeLanguage(e.target.value)}
                            data-testid="wa-reply-language"
                            aria-label="Reply language"
                            title="Reply language"
                            className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white focus:border-emerald-500 focus:outline-none">
                            {LANGUAGES.map((l) => (
                                <option key={l.id} value={l.id}>{l.label}</option>
                            ))}
                        </select>
                        <button
                            type="button"
                            onClick={generate}
                            disabled={busy}
                            data-testid="wa-reply-generate"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#1E3A8A] text-white text-sm font-semibold rounded-lg hover:bg-[#1E40AF] disabled:opacity-60">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            {busy ? "Generating…" : (message ? "Regenerate Reply" : "Generate Reply")}
                        </button>
                    </div>

                    <div className="relative">
                        <textarea
                            value={message}
                            onChange={(e) => { setMessage(e.target.value); setDirty(true); }}
                            rows={7}
                            placeholder={`Click "Generate Reply" to draft a ${templateLabel} message. You can edit it here before sending.`}
                            data-testid="wa-reply-textarea"
                            className="w-full px-3 py-2.5 text-sm rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-y whitespace-pre-wrap"
                        />
                        {dirty && message && (
                            <span className="absolute top-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 rounded-full px-1.5 py-0.5" data-testid="wa-reply-edited-badge">
                                <Pencil className="w-2.5 h-2.5" /> edited
                            </span>
                        )}
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={copy}
                            disabled={!message.trim()}
                            data-testid="wa-reply-copy"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 text-slate-700 text-sm font-semibold rounded-lg hover:border-slate-300 disabled:opacity-50">
                            <Copy className="w-4 h-4" /> Copy Message
                        </button>
                        {waLink ? (
                            <a
                                href={waLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={logSent}
                                data-testid="wa-reply-send"
                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 ${message.trim() ? "" : "opacity-60 pointer-events-none"}`}>
                                <Send className="w-4 h-4" /> Send on WhatsApp
                            </a>
                        ) : (
                            <button
                                type="button"
                                disabled
                                data-testid="wa-reply-send-disabled"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-200 text-slate-500 text-sm font-semibold rounded-lg cursor-not-allowed"
                                title="No phone number on this record">
                                <Send className="w-4 h-4" /> Send on WhatsApp
                            </button>
                        )}
                        {!initialPhone && (
                            <span className="text-[11px] text-rose-600">
                                Add a phone number above to enable WhatsApp send.
                            </span>
                        )}
                        {canSend && (
                            <span className="text-[11px] text-slate-500 ml-auto">
                                Opens wa.me with the drafted message pre-filled — you still tap Send inside WhatsApp.
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
